import { SearchIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  InputGroup,
  InputRightElement,
  Input,
  useColorModeValue,
  Center,
  Spinner,
  Tooltip,
  Button,
  Spacer,
} from '@chakra-ui/react';
import ProgressDisplay from 'components/course/ProgressDisplay';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { FaChartBar } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { ReducerStatus } from 'utils/constants';
import { SiMicrosoftexcel } from 'react-icons/si';
import exportAsCsv from 'utils/exportCsv';

const videoColumns = [
  {
    name: 'Id',
    selector: (row) => row.subQuest.id,
    sortable: true,
  },
  {
    name: 'Name',
    selector: (row) => row.subQuest.name,
    sortable: true,
  },
];

const ProgressionColumn = (progressionRecords, usersCount) => {
  const getSubQuestProgress = (subQuestId) => {
    const userCompletedActivity = progressionRecords.filter(
      (pr) => pr.dateFinished !== null && pr.subQuestId === subQuestId
    ).length;

    return (userCompletedActivity / usersCount) * 100;
  };

  return {
    name: 'Progress',
    sortable: true,
    selector: (row) => getSubQuestProgress(row.subQuest.id),
    cell: (row) => (
      <ProgressDisplay
        w="100%"
        my={2}
        isLoading={false}
        percentage={getSubQuestProgress(row.subQuest.id)}
        count={0}
        countLabel=""
      />
    ),
  };
};

const ActionColumn = (handleViewProgress) => {
  return {
    name: 'Action',
    sortable: false,
    cell: (row) => (
      <Tooltip label="View SubQuest Progress" fontSize="xs">
        <Button
          colorScheme="blue"
          size="xs"
          onClick={() => handleViewProgress(row.subQuest.id)}
        >
          <FaChartBar />
        </Button>
      </Tooltip>
    ),
  };
};

function SubQuestRecord({ typeId, handleViewProgress }) {
  const records = useSelector((state) => state.class.records);
  const progressionRecords = useSelector(
    (state) => state.class.records.progressionRecords
  );
  const usersCount = useSelector((state) => state.class.records.users.length);
  const getClassRecords = useSelector((state) => state.class.getClassRecords);

  const [filterText, setFilterText] = useState('');
  const [questSubQuests, setQuestSubQuests] = useState([]);

  const filteredData = questSubQuests.filter(
    (item) =>
      (item.subQuest.name &&
        item.subQuest.name.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.subQuest.id &&
        item.subQuest.id
          .toString()
          .toLowerCase()
          .includes(filterText.toLowerCase()))
  );

  const handleExport = () => {
    exportAsCsv(
      progressionRecords.filter((pr) => pr.typeId === typeId),
      `class-report-subquest-type-${typeId}`
    );
  };

  useEffect(() => {
    setQuestSubQuests(
      records.questSubQuests.filter((qsq) => qsq.subQuest.type.id === typeId)
    );
  }, [typeId, records.questSubQuests]);

  return (
    <Box>
      <Flex m={2} gap={2} align="center">
        <Button
          onClick={handleExport}
          rightIcon={<SiMicrosoftexcel />}
          colorScheme="blue"
          variant="outline"
        >
          Export
        </Button>
        <Spacer />
        <InputGroup width={{ base: '100%', md: '350px' }}>
          <InputRightElement pointerEvents="none">
            <SearchIcon color="gray.300" />
          </InputRightElement>
          <Input
            name="query"
            placeholder="Search"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </InputGroup>
      </Flex>

      <DataTable
        theme={useColorModeValue('default', 'dark')}
        progressPending={getClassRecords.status === ReducerStatus.LOADING}
        columns={[
          ...videoColumns,
          ProgressionColumn(progressionRecords, usersCount),
          ActionColumn(handleViewProgress),
        ]}
        data={filteredData}
        progressComponent={
          <Center my={10}>
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color="blue.500"
              size="xl"
            />
          </Center>
        }
        pagination
      />
    </Box>
  );
}

SubQuestRecord.propTypes = {
  typeId: PropTypes.number.isRequired,
  handleViewProgress: PropTypes.func.isRequired,
};

export default SubQuestRecord;
